import { Fragment, memo, SyntheticEvent, useContext, useEffect, useRef, useState } from "react";
import { Table as BootstrapTable, Button } from "react-bootstrap";
import { BsTrash } from "react-icons/bs";
import { FaCheck } from "react-icons/fa";
import { LiaPlusSolid } from "react-icons/lia";
import { RiArrowDownSLine, RiArrowUpSLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";

import ConfirmationPopup from "../../../shared/components/confirmation-popup/confirmation-popup";
import Spinner from "../../../shared/components/spinner/spinner";
import TextInput from "../../../shared/components/text-input/text-input";
import Tooltip from "../../../shared/components/tooltip/tooltip";
import AuthContext from "../../../shared/context/auth-context";
import { IDataType } from "../../../shared/definitions/datatypes";
import useHttp from "../../../shared/hooks/use-http";
import { deleteMultiple, getAllData, postSingle } from "../../../shared/utils/api";
import Checkbox from "../checkbox/checkbox";
import "./data-table.scss";
import { IDataUrl, IDataUrlPost } from "../../utils";
import { validateLoadingStatusNew } from "../../../shared/utils/utils";
import { roleWrite } from "../../../shared/constants/user-roles";

export interface IAnyString {
  [index: string]: string;
}

export interface ITableRowData {
  id: string;
  [index: string]: any; // eslint-disable-line
}

export interface IColumn {
  key: string;
  displayName: string;
  dataType: IDataType;
  sortable?: boolean;
}

export interface ITableConfig {
  columns: IColumn[];
  dataUrl: IDataUrl | IDataUrlPost;
  deleteUrl?: IDataUrl;
  defaultSortCol: string;
  selectionMode: "multi" | "single" | "off";
  navigate_to?: string;
  navigateToBaseUrl?: string;
  preSelectedIds?: string[];
  title?: string;
  previewMode?: boolean;
  isRowClickable?: boolean;
  style?: "default" | "compact";
  defaultSortDirection?: "asc" | "desc";
  onBeforeRowClick?: (rowId: string) => Promise<boolean>;
  onSelectionChange?: (selectedIds: string[]) => void;
  onActionClick?: (action_key: string, rowId: string) => void;
}

export interface ISortedBy {
  column: undefined | string;
  direction: undefined | string;
}

const DataTable = ({
  columns,
  defaultSortCol,
  defaultSortDirection = "asc",
  dataUrl,
  deleteUrl,
  navigate_to,
  navigateToBaseUrl,
  selectionMode = "multi",
  preSelectedIds,
  title,
  previewMode,
  style = "default",
  isRowClickable,
  onBeforeRowClick,
  onSelectionChange,
  onActionClick
}: ITableConfig) => {
  const limit = 50;
  const [fullyLoaded, setFullyLoaded] = useState(false);

  const [checkAll, setCheckAll] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [data, setData] = useState<ITableRowData[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>(preSelectedIds || []);
  const [scrolled, setScrolled] = useState(false);
  const [showConfirmationPopup, setConfirmationPopup] = useState(false);
  const [sortedByCol, setSortedByCol] = useState<ISortedBy>({ column: defaultSortCol, direction: defaultSortDirection });
  const [waitingForRowClicked, setWaitingForRowClicked] = useState(false);
  const [paginationOffset, setPaginationOffset] = useState(0);
  const authCtx = useContext(AuthContext);
  const isInternalUpdate = useRef(false);
  const [blockInput, setBlockInput] = useState<boolean>(true);
  
  const {
    sendRequest: getTableData,
    status: getTableDataStatus,
    data: getTableDataResult,
    error: getTableDataError
  } = useHttp(
    "payload" in dataUrl ? postSingle : getAllData,
    false,
    authCtx.token,
    dataUrl.transformType,
    dataUrl.backend,
  );

  const { sendRequest: deleteMultipleRequest, status: deleteMultipleStatus, data: deleteMultipleData, error: deleteMultipleError } = useHttp(deleteMultiple, false, authCtx.token, deleteUrl?.transformType, deleteUrl?.backend);

  useEffect(() => {
    const rolesArray = authCtx.selectedTenant.roles;
    if (rolesArray.indexOf(roleWrite) !== -1) {
      setBlockInput(false);
    }
  }, [authCtx.selectedTenant, authCtx.systemAdmin]);

  useEffect(() => {
    if (searchInputValue && !fullyLoaded && filterRowsOnSearchInput().length <= limit && getTableDataStatus === "completed" && !getTableDataError && getTableDataResult) {
      setPaginationOffset(paginationOffset + 1);
    }
  }, [searchInputValue, getTableDataResult, getTableDataStatus, getTableDataError]);

  const node = useRef<any>(); // eslint-disable-line
  const navigate = useNavigate();

  useEffect(() => {
    if (previewMode) {
      const dummyData: ITableRowData[] = [];
      for (let i = 0; i < 2; i++) {
        dummyData.push(
          columns.reduce((acc, curr) => {
            acc[curr.key] = "...";
            return acc;
          }, { id: i.toString() } as ITableRowData)
        );
      }
      setData(dummyData);
      return;
    }

    if (!fullyLoaded) {
      let queryString = `?limit=${limit}&offset=${paginationOffset * limit}`;
      if (sortedByCol.column) {
        queryString = `${queryString}&order_by=${sortedByCol.column} ${sortedByCol.direction}`;
      }
      if (dataUrl.queryString) {
        queryString = `${queryString}&${dataUrl.queryString}`;
      }

      if ("payload" in dataUrl) {
        getTableData(
          `${dataUrl.url}${queryString}`,
          {
            ...dataUrl.payload,
            rowData: [],
          },
          undefined,
          "none",
        );
      } else {
        getTableData(`${dataUrl.url}${queryString}`, undefined, undefined, "none");
      }
    }
  }, [paginationOffset, sortedByCol]);

  useEffect(() => {
    if (getTableDataStatus === "completed" && !getTableDataError && getTableDataResult) {
      if (getTableDataResult.rows.length < limit) {
        setFullyLoaded(true);
      }

      if (paginationOffset === 0) {
        setData(getTableDataResult.rows);
      } else {
        setData([...data, ...getTableDataResult.rows]);
      }
    }
  }, [getTableDataStatus, getTableDataResult]);

  useEffect(() => {
    if (deleteMultipleStatus === "completed" && !deleteMultipleError) {
      resetTable();
      setData(deleteMultipleData.rows);
    }
  }, [deleteMultipleStatus]);

  useEffect(() => {
    if (preSelectedIds && !isInternalUpdate.current) {
      const isDifferent =
        preSelectedIds.length !== selectedRows.length ||
        preSelectedIds.some(id => !selectedRows.includes(id));

      if (isDifferent) {
        setSelectedRows(preSelectedIds);
      }
    }
    isInternalUpdate.current = false;
  }, [preSelectedIds]);

  useEffect(() => {
    if (onSelectionChange && isInternalUpdate.current) {
      onSelectionChange(selectedRows);
    }
  }, [selectedRows]);

  const handleOnSelectRow = (id: string) => {
    isInternalUpdate.current = true;

    const result = [...selectedRows];

    if (selectionMode === "multi") {
      if (result.indexOf(id) === -1) {
        result.push(id);
        setSelectedRows(result);
      } else {
        const filteredListOfIds = result.filter((el) => el !== id);
        setSelectedRows(filteredListOfIds);
      }
    } else {
      if (result.indexOf(id) === -1) {
        setSelectedRows([id]);
      } else {
        setSelectedRows([]);
      }
    }
  };

  const handleOnSelectAllRows = () => {
    if (selectionMode === "multi") {
      isInternalUpdate.current = true;

      if (data && !checkAll) {
        const result = data.map((row) => row.id);
        setSelectedRows(result);
        setCheckAll(true);
      } else {
        setSelectedRows([]);
        setCheckAll(false);
      }
    }
  };

  const isSelected = (id: string) => {
    return selectedRows.indexOf(id) !== -1;
  };

  const rowClicked = (rowId: string) => {
    switch (selectionMode) {
      case "off":
        return;
      case "multi":
        if (isRowClickable == false) {
          isInternalUpdate.current = true;
        } else {
          if (navigateToBaseUrl) {
            navigate(`${navigateToBaseUrl}/${rowId}`);
          } else {
            navigate(rowId);
          }
        }
        break;
      case "single":
        isInternalUpdate.current = true;

        if (selectedRows.includes(rowId)) {
          setSelectedRows([]);
        } else {
          setSelectedRows([rowId]);
        }
        break;
    }
  };

  const handleOnRowClick = (evt: React.MouseEvent, rowId: string) => {
    evt.stopPropagation();

    if (onBeforeRowClick && !waitingForRowClicked) {
      setWaitingForRowClicked(true);
      onBeforeRowClick(rowId).then((result) => {
        if (!result) {
          return;
        }
        rowClicked(rowId);
      }).finally(() => {
        setWaitingForRowClicked(false);
      });
    }
    if (!onBeforeRowClick) {
      rowClicked(rowId);
    }
  };

  const filterRowsOnSearchInput   = () => {
    return data.filter(row => {
      const columnsKeys = columns.map(column => column.key);
      let resultFilteredForMatching: IAnyString = {};
      let concatResultFilteredForMatching = "";

      columnsKeys.forEach(key => {
        resultFilteredForMatching = { ...resultFilteredForMatching, [key]: row[key] };
      });
      for (const key in resultFilteredForMatching) {
        concatResultFilteredForMatching = concatResultFilteredForMatching.toString().toLowerCase() + " " + (resultFilteredForMatching[key] ? resultFilteredForMatching[key].toString().toLowerCase() : "");
      }

      return concatResultFilteredForMatching.indexOf(searchInputValue.toLowerCase()) > -1;
    });
  };

  const renderRows = () => {
    if (data.length === 0) {
      return <tr className="dataTable-row dataTable-rowNoData"><td colSpan={columns.length + (selectionMode !== "off" ? 1 : 0)}><span className="dataTable-noData">No data to be displayed...</span></td></tr>;
    }

    const result = filterRowsOnSearchInput();
    return result.map((rowData: ITableRowData, i: number) => {
      return (
        <tr
          className="dataTable-row"
          key={i}
          onClick={(e) => handleOnRowClick(e, rowData.id)}
        >
          {selectionMode !== "off" && <td key={rowData.id}>
            <span>
              <Checkbox
                checked={isSelected(rowData.id)}
                onClick={() => handleOnSelectRow(rowData.id)}
              />
            </span>
          </td>
          }

          {columns.map((column) => {
            let columnData = rowData[column.key];

            if (column.dataType.type === "action") {
              columnData = (
                <div className="dataTable-actionButtons">
                  <Button onClick={(e) => {
                    e.stopPropagation();
                    onActionClick?.(column.key, rowData.id);
                  }}
                  disabled={column.dataType.disabled && column.dataType.disabled(rowData)}
                  >{column.dataType.buttonText}</Button>
                </div>
              );
            }
            else {
              if (columnData === true) {
                columnData = (
                  <Fragment>
                    <FaCheck className="dataTable-activeIcon" />
                  </Fragment>
                );
              }
              else if (columnData === false) {
                columnData = (
                  <Fragment></Fragment>
                );
              }
            }
            if (column.dataType.type === "dateTime" && columnData) {
              const parsedDate = new Date(columnData).toLocaleString();
              if (parsedDate === "Invalid Date") {
                columnData = rowData[column.key];
              } else {
                columnData = parsedDate;
              }
            }
            if (
              column.dataType.type === "number" &&
              columnData !== null &&
              columnData !== undefined &&
              column.dataType.decimalPlaces !== undefined
            ) {
              const parsedNumber = parseFloat(columnData)
              if (isNaN(parsedNumber)) {
                columnData = rowData[column.key];
              } else {
                columnData = parsedNumber.toFixed(column.dataType.decimalPlaces);
              }
            }
            return <td key={column.key}><span>{columnData}</span></td>;
          })}
        </tr>
      );
    });
  };

  const resetTable = () => {
    setSelectedRows([]);
    setFullyLoaded(false);
    setPaginationOffset(0);
  };

  const sortColumVals = (key: string) => {
    let defineDirection;
    if (sortedByCol.column === key && sortedByCol.direction === "asc") {
      defineDirection = "desc";
    } else {
      defineDirection = "asc";
    }

    setSortedByCol({ column: key, direction: defineDirection });
    resetTable();
  };

  const defineSortingArrows = (key: string) => {
    if (sortedByCol.column === key && sortedByCol.direction === "desc") {
      return <RiArrowUpSLine />;
    } else {
      return <RiArrowDownSLine />;
    }
  };

  const onTableScroll = (evt: SyntheticEvent<HTMLDivElement>) => {
    const bottom = evt.currentTarget.scrollHeight - evt.currentTarget.scrollTop === evt.currentTarget.clientHeight;
    if (bottom) {
      setPaginationOffset(prev => prev + 1);
    }

    setScrolled(evt.currentTarget.scrollTop > 0);
  };

  const handleOnDeleteConfirmSubmit = () => {
    if (!deleteUrl) {
      return;
    }
    let queryString = `?order_by=${sortedByCol.column} ${sortedByCol.direction}&limit=${limit}&offset=${paginationOffset * limit}`;

    if (deleteUrl.queryString) {
      queryString = `${queryString}&${deleteUrl.queryString}`;
    }
    deleteMultipleRequest(`${deleteUrl.url}${queryString}`, { ids: selectedRows });
  };

  return (
    <div className={`dataTable dataTable-${style}`}>
      {validateLoadingStatusNew([deleteMultipleStatus, getTableDataStatus]) && <Spinner />}
      <ConfirmationPopup
        confirmButtonText={"Delete"}
        text={"Are you sure you want to delete the selected rows?"}
        show={showConfirmationPopup}
        handlePopupClosed={() => setConfirmationPopup(false)}
        handleSaveClick={handleOnDeleteConfirmSubmit}
      />
      <div className="dataTable-buttonsGroup">
        <div className="dataTable-header">
          {title && <h2>{title}</h2>}
        </div>
        <div className="dataTable-buttons">
          <TextInput
            type={"text"}
            id={"dataTable-searchInput"}
            required={false}
            inputKey={"dataTable-searchInput"}
            autofocus={false}
            placeholder={"Type to filter"}
            handleOnChange={(targetVal) => setSearchInputValue(targetVal)}
          />
          {(dataUrl && deleteUrl) &&
            <Tooltip content={"Delete"} direction="top" delay={350} >
              <Button name="delete"
                disabled={!deleteUrl || selectedRows.length === 0}
                onClick={() => setConfirmationPopup(true)}
                className="dataTable-delete"
                variant="primary"
              >
                <BsTrash />
              </Button>
            </Tooltip>
          }
          {(navigate_to && !blockInput) &&
            <Tooltip content={"Add New"} direction="top" delay={350} >
              <Button
                name="add"
                onClick={() => navigate(navigate_to)}
                className="dataTable-new"
                variant="primary">
                <LiaPlusSolid />
              </Button>
            </Tooltip>
          }
        </div>
      </div>
      <div ref={node} onScroll={onTableScroll} className={`dataTable-container ${scrolled ? "scrolled" : ""}`}>
        {
          <BootstrapTable size="sm" borderless striped>
            <thead>
              <tr>
                {
                  selectionMode !== "off" ?
                    <th className="dataTable-actionTh" key={"actions"}>
                      <span>
                        {selectionMode === "multi" ? (
                          <Checkbox
                            checked={selectedRows.length === data.length}
                            onClick={handleOnSelectAllRows}
                          />
                        ) : null}
                      </span>
                    </th>
                    : ""
                }
                {columns.map((column) => (
                  <th
                    onClick={() => column.sortable !== false && sortColumVals(column.key)}
                    className={`${column.sortable !== false ? "dataTable-sortable" : ""} ${column.key === sortedByCol.column ? "dataTable-selectedCol" : ""}`}
                    key={column.key}>
                    <span>
                      {column.displayName} {column.sortable !== false && defineSortingArrows(column.key)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{renderRows()}</tbody>
          </BootstrapTable>
        }
      </div>
    </div>
  );
};

export default memo(DataTable);